import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { attemptChunkRecovery, isChunkLoadError } from '../utils/chunkLoadRecovery';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);

        if (isChunkLoadError(error)) {
            attemptChunkRecovery(error);
        }
    }

    public render() {
        const isRecoverableChunkError = isChunkLoadError(this.state.error);

        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-800 p-6">
                    <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center mx-auto mb-6">
                            <AlertTriangle className="text-red-500 w-8 h-8" />
                        </div>

                        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-2">Ops! Algo deu errado.</h1>
                        <p className="text-slate-500 dark:text-slate-400 dark:text-slate-500 mb-6">
                            {isRecoverableChunkError
                                ? 'Detectamos uma nova versão do sistema. Vamos atualizar a tela para continuar.'
                                : 'O sistema encontrou um erro inesperado. Tente recarregar a página.'}
                        </p>

                        <div className="bg-slate-100 dark:bg-slate-700 p-4 rounded-lg text-left mb-6 overflow-auto max-h-32">
                            <code className="text-xs text-slate-600 dark:text-slate-400 dark:text-slate-500 font-mono">
                                {this.state.error?.message || 'Erro desconhecido'}
                            </code>
                        </div>

                        <button
                            onClick={() => {
                                if (!attemptChunkRecovery(this.state.error)) {
                                    window.location.reload();
                                }
                            }}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
                        >
                            <RefreshCw size={18} />
                            {isRecoverableChunkError ? 'Atualizar Sistema' : 'Recarregar Sistema'}
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
